import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { FileText, Download, X, Building2, User, Home, Flag } from 'lucide-react';

const DEAL_STATUS_LABELS = { under_contract: 'Under Contract (Offer Accepted)', submitting_offer: 'Submitting an Offer', still_shopping: 'Still Shopping' };
const FUNDING_TIMELINE_LABELS = { asap: 'ASAP', '1_2_weeks': '1 - 2 weeks', '2_4_weeks': '2 - 4 weeks', flexible: 'Flexible' };

const LoanQuotes = ({ lob }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(() => {
    if (!lob) return;
    setLoading(true); setErr('');
    api.get('/loan-quotes', { params: { lob_id: lob.id } })
      .then(({ data }) => setQuotes(data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [lob]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => {
    setDetailLoading(true);
    api.get(`/loan-quotes/${id}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setDetailLoading(false));
  };

  const downloadPdf = (id) => {
    setPdfBusy(true);
    api.get(`/loan-quotes/${id}/pdf`, { responseType: 'blob' })
      .then(({ data }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        a.download = `loan-quote-${id.slice(0, 8)}.pdf`;
        a.click();
      })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setPdfBusy(false));
  };

  const loanTypeLabel = (t) => (t === 'fix_and_flip' ? 'Fix & Flip' : 'DSCR');

  return (
    <div className="module-pane" data-testid="loan-quotes-module">
      <div className="page-toolbar">
        <span className="page-title">Loan Quotes</span>
        <span style={{ fontSize: 11, color: 'var(--text-faded)', marginLeft: 10 }}>
          Submissions from the LT Capital "Get a Loan Quote" wizard
        </span>
      </div>

      {err && <div className="module-banner err" data-testid="loan-quotes-error">{err}</div>}

      <div className="leads-scroll">
        {loading ? (
          <div className="empty-state" data-testid="loan-quotes-loading"><div className="ttl">Loading loan quotes…</div></div>
        ) : quotes.length === 0 ? (
          <div className="empty-state" data-testid="loan-quotes-empty">
            <div className="ttl">No loan quotes yet</div>
            <div className="sub">Submissions from the website's "Get a Loan Quote" wizard will show up here.</div>
          </div>
        ) : (
          <table className="tbl leads-tbl" data-testid="loan-quotes-table">
            <thead>
              <tr>
                <th>Borrower</th>
                <th>Loan Type</th>
                <th>Property</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`loan-quote-row-${q.id}`} style={{ cursor: 'pointer' }} onClick={() => openDetail(q.id)}>
                  <td><b>{q.borrower_name || '—'}</b><div style={{ fontSize: 11, color: 'var(--text-faded)' }}>{q.borrower_email}</div></td>
                  <td><span className="spill" style={{ background: 'var(--orange-light)', color: '#7a3c00' }}>{loanTypeLabel(q.loan_type)}</span></td>
                  <td>{q.property_address || '—'}{q.property_count > 1 ? ` +${q.property_count - 1} more` : ''}</td>
                  <td>{q.created_at ? new Date(q.created_at).toLocaleString() : '—'}</td>
                  <td>
                    <button className="icon-btn" onClick={(e) => { e.stopPropagation(); openDetail(q.id); }} data-testid={`loan-quote-view-${q.id}`} title="View">
                      <FileText size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(detail || detailLoading) && (
        <div className="modal-overlay-v2" onClick={() => setDetail(null)}>
          <div className="modal-card" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()} data-testid="loan-quote-detail-modal">
            <div className="modal-head">
              <b>Loan Quote — {detail ? loanTypeLabel(detail.loan_type) : ''}</b>
              <button className="icon-btn" onClick={() => setDetail(null)} data-testid="loan-quote-detail-close"><X size={16} /></button>
            </div>

            {detailLoading || !detail ? (
              <div className="empty-state"><div className="ttl">Loading…</div></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxHeight: '65vh', overflowY: 'auto' }}>
                <div style={{ fontSize: 11, color: 'var(--text-faded)' }}>
                  Submitted {detail.created_at ? new Date(detail.created_at).toLocaleString() : '—'}
                </div>

                <div>
                  <div className="qd-section-title"><Building2 size={13} /> Entity</div>
                  {detail.entity && detail.entity.status === 'add_entity' ? (
                    <div className="qd-kv-grid">
                      <div className="qd-kv"><span>Entity Name</span><b>{detail.entity.name || '—'}</b></div>
                      <div className="qd-kv"><span>Entity Type</span><b>{detail.entity.entity_type || '—'}</b></div>
                      <div className="qd-kv"><span>Location</span><b>{[detail.entity.city, detail.entity.state].filter(Boolean).join(', ') || '—'}</b></div>
                    </div>
                  ) : (
                    <div className="qd-note">No entity provided — quoted to the individual borrower(s) directly.</div>
                  )}
                </div>

                {(detail.borrowers || []).map((b, i) => (
                  <div key={i}>
                    <div className="qd-section-title"><User size={13} /> Borrower {i + 1}</div>
                    <div className="qd-kv-grid">
                      <div className="qd-kv"><span>Name</span><b>{b.first} {b.last}</b></div>
                      <div className="qd-kv"><span>Email</span><b>{b.email || '—'}</b></div>
                      <div className="qd-kv"><span>Best Contact Number</span><b>{b.phone || '—'}</b></div>
                      <div className="qd-kv"><span>FICO</span><b>{b.fico || '—'}</b></div>
                      <div className="qd-kv"><span>Guarantor</span><b>{b.guarantor ? 'Yes' : 'No'}</b></div>
                    </div>
                  </div>
                ))}

                {(detail.properties || []).map((p, i) => (
                  <div key={i}>
                    <div className="qd-section-title"><Home size={13} /> Property {i + 1}</div>
                    <div className="qd-kv-grid">
                      <div className="qd-kv" style={{ gridColumn: '1 / -1' }}><span>Address</span><b>{p.address}</b></div>
                      <div className="qd-kv"><span>Type</span><b>{p.property_type || '—'}</b></div>
                      <div className="qd-kv"><span>Purchase Price</span><b>{fmtMoney(p.purchase_price)}</b></div>
                      <div className="qd-kv"><span>Construction Budget</span><b>{fmtMoney(p.construction_budget)}</b></div>
                      <div className="qd-kv"><span>ARV</span><b>{fmtMoney(p.arv)}</b></div>
                    </div>
                  </div>
                ))}

                {detail.deal && (
                  <div>
                    <div className="qd-section-title"><Flag size={13} /> Deal</div>
                    <div className="qd-kv-grid">
                      <div className="qd-kv"><span>Status</span><b>{DEAL_STATUS_LABELS[detail.deal.status] || detail.deal.status}</b></div>
                      {detail.deal.status === 'under_contract' && (
                        <div className="qd-kv"><span>Estimated Closing Date</span><b>{detail.deal.closing_date || '—'}</b></div>
                      )}
                      {detail.deal.status === 'submitting_offer' && (
                        <div className="qd-kv"><span>Offer Due By</span><b>{detail.deal.offer_due_date || '—'}</b></div>
                      )}
                      <div className="qd-kv"><span>Funding Timeline</span><b>{FUNDING_TIMELINE_LABELS[detail.deal.funding_timeline] || detail.deal.funding_timeline || '—'}</b></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button className="dp-btn" onClick={() => setDetail(null)}>Close</button>
              {detail && (
                <button className="dp-btn primary" disabled={pdfBusy} onClick={() => downloadPdf(detail.id)} data-testid="loan-quote-download-pdf-btn">
                  <Download size={13} style={{ marginRight: 5 }} />{pdfBusy ? 'Preparing…' : 'Download PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanQuotes;
